import { useContext } from 'react'
import type { CSSProperties } from 'react'
import { twMerge } from 'tailwind-merge'
import { I18nContext } from '../../i18n/I18nContext'
import { authCopy } from '../../i18n/authCopy'

/**
 * ActivityIndicator - the one loading indicator: eight spokes fading in turn around a
 * circle, the way Apple devices show a wait. Ported from the SMS system's `.app-spinner`
 * (8 bars, 45 degrees apart, 1s linear cycle, each bar 1 -> 0.2 opacity, head turning
 * clockwise). The CSS lives in index.css under `.dhtn-activity`.
 *
 * Colour is `currentColor`: set the text colour on the parent (a primary button's label
 * colour, --dhtn-text-muted, --dhtn-action-ink) and the spokes follow.
 *
 * Crisp edges: spokes are whole pixels, about 1/8 of the box (the SMS proportion),
 * rounded down at the half: 16 -> 2px, 20 -> 2px, 24 -> 3px, 32 -> 4px, 40 -> 5px.
 * Length is 0.3 of the box, whole pixels. Even widths (16/20/32) sit on pixel edges at
 * 1x; 24 and 40 do from 2x up, which is every phone.
 *
 * Accessibility: by default a role="status" region with a visually hidden label from
 * authCopy.loading (or `label`). Pass `decorative` when the parent already announces the
 * wait (for example a status region that shows its label as visible text).
 */
export function ActivityIndicator({
    size = 20,
    label,
    decorative = false,
    className,
}: {
    size?: number
    label?: string
    decorative?: boolean
    className?: string
}) {
    const context = useContext(I18nContext)
    const documentLanguage = typeof document === 'undefined' ? 'en' : document.documentElement.lang
    const language = context?.language ?? (documentLanguage === 'ar' || documentLanguage === 'ha' ? documentLanguage : 'en')
    const loadingLabel = label ?? authCopy[language].loading

    const spokeWidth = Math.max(2, Math.round((size - 1) / 8))
    const style = {
        '--ai-size': `${size}px`,
        '--ai-w': `${spokeWidth}px`,
        '--ai-l': `${Math.round(size * 0.3)}px`,
    } as CSSProperties
    const spokes = (
        <span className={twMerge('dhtn-activity', decorative ? className : undefined)} style={style} aria-hidden="true">
            <i /><i /><i /><i /><i /><i /><i /><i />
        </span>
    )
    if (decorative) return spokes
    return (
        <span role="status" className={twMerge('inline-flex items-center justify-center', className)}>
            {spokes}
            <span className="sr-only">{loadingLabel}</span>
        </span>
    )
}

export default ActivityIndicator
